// ./src/components/features/Submissions/SubmissionDetailsDrawer/SubmissionFormData.tsx
import React, { useMemo, useState, useCallback } from "react";
import {
  Stack,
  Text,
  Alert,
  Box,
  Group,
  TextInput,
  Textarea,
  Badge,
  Paper,
  ActionIcon,
  Tooltip,
  Image,
  Modal,
  Button,
  CopyButton,
  Checkbox,
  SimpleGrid,
  Code,
  ScrollArea,
} from "@mantine/core";
import { Submission, Form, FormField, SubmissionFile } from "@/utils/api";
import { AppIcon } from "@/components/generic";
import { FormFieldTypeIcon } from "@/components/features";
import { CoordinatesFieldValue } from "./CoordinatesFieldValue";
import { FIELD_TYPE_IDS } from "@/config";
import { formatDateTime } from "@/utils";

export interface SubmissionFormDataProps {
  /** Submission to display */
  submission: Submission | null;

  /** Form configuration */
  form: Form | null;

  /** Form fields for labels */
  formFields?: FormField[];

  /** Hide copy buttons (e.g., when generating PDF) */
  hideCopyButtons?: boolean;

  /** Hide file preview/download actions (e.g., when generating PDF) */
  hideFileActions?: boolean;
}

// ============================================================================
// Helper Functions
// ============================================================================

const formatFileSize = (bytes: number | string): string => {
  const numBytes = typeof bytes === "string" ? parseInt(bytes, 10) : bytes;
  if (isNaN(numBytes) || numBytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(numBytes) / Math.log(k));
  return `${(numBytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
};

const isImageFile = (file: SubmissionFile): boolean => {
  if (!file.mimeType) return false;
  return file.mimeType.startsWith("image/");
};

const isVideoFile = (file: SubmissionFile): boolean => {
  if (!file.mimeType) return false;
  return file.mimeType.startsWith("video/");
};

const isLongTextValue = (value: any, fieldType?: number): boolean => {
  if (fieldType === FIELD_TYPE_IDS.LONG_TEXT) return true;
  if (typeof value === "string" && value.length > 100) return true;
  if (typeof value === "string" && value.includes("\n")) return true;
  return false;
};

const getValueAsString = (value: any): string => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
};

const getClickableHref = (
  value: string,
  fieldType?: number
): { href: string; target?: string } | null => {
  if (fieldType === FIELD_TYPE_IDS.EMAIL) {
    return { href: `mailto:${value}` };
  }
  if (fieldType === FIELD_TYPE_IDS.PHONE) {
    return { href: `tel:${value.replace(/[\s\-\(\)]/g, "")}` };
  }
  if (fieldType === FIELD_TYPE_IDS.URL) {
    const url = value.startsWith("http") ? value : `https://${value}`;
    return { href: url, target: "_blank" };
  }
  return null;
};

// ============================================================================
// Sub-Components
// ============================================================================

interface FieldValueInputProps {
  fieldName: string;
  label: string;
  value: any;
  fieldType?: number;
  hideCopyButton?: boolean;
  isOrphaned?: boolean;
}

const FieldValueInput: React.FC<FieldValueInputProps> = ({
  //fieldName,
  label,
  value,
  fieldType,
  hideCopyButton = false,
  isOrphaned = false,
}) => {
  const stringValue = getValueAsString(value);
  const isEmpty = stringValue === "";
  const isLongText = isLongTextValue(value, fieldType);
  const isBoolean = typeof value === "boolean";
  const isObject =
    typeof value === "object" && value !== null && !Array.isArray(value);
  const clickableInfo =
    !isEmpty && typeof value === "string"
      ? getClickableHref(value, fieldType)
      : null;

  // Copy button component
  const CopyButtonSection = !hideCopyButton && !isEmpty && (
    <CopyButton value={stringValue}>
      {({ copied, copy }) => (
        <Tooltip label={copied ? "Copied!" : "Copy"} withArrow position="left">
          <ActionIcon
            variant="subtle"
            color={copied ? "teal" : "gray"}
            onClick={copy}
            size="sm"
          >
            <AppIcon iconName={copied ? "Check" : "Copy"} size={14} />
          </ActionIcon>
        </Tooltip>
      )}
    </CopyButton>
  );

  // Icon section
  const IconSection = (
    <FormFieldTypeIcon
      fieldType={fieldType}
      size={16}
      color={
        isOrphaned
          ? "var(--mantine-color-yellow-6)"
          : "var(--mantine-color-gray-5)"
      }
    />
  );

  // Handle boolean values with a checkbox
  if (isBoolean) {
    return (
      <Box>
        <Text size="sm" fw={500} mb={4}>
          {label}
        </Text>
        <Group gap="xs" align="center">
          <FormFieldTypeIcon
            fieldType={fieldType}
            size={16}
            color="var(--mantine-color-gray-5)"
          />
          <Checkbox
            checked={value}
            readOnly
            label={value ? "Yes" : "No"}
            styles={{
              input: { cursor: "default" },
              label: { cursor: "default" },
            }}
          />
        </Group>
      </Box>
    );
  }

  // Handle object values (JSON display)
  if (isObject) {
    return (
      <Box>
        <Group justify="space-between" align="center" mb={4}>
          <Group gap="xs">
            {IconSection}
            <Text size="sm" fw={500}>
              {label}
            </Text>
          </Group>
          {CopyButtonSection}
        </Group>
        <Paper
          withBorder
          p="xs"
          style={{
            backgroundColor: "var(--mantine-color-gray-0)",
            fontFamily: "monospace",
            fontSize: "0.8rem",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {stringValue}
        </Paper>
      </Box>
    );
  }

  // Handle long text with Textarea
  if (isLongText) {
    return (
      <Box>
        <Group justify="space-between" align="center" mb={4}>
          <Group gap="xs">
            {IconSection}
            <Text size="sm" fw={500}>
              {label}
            </Text>
          </Group>
          {CopyButtonSection}
        </Group>
        <Textarea
          value={isEmpty ? "—" : stringValue}
          readOnly
          autosize
          minRows={2}
          maxRows={10}
          styles={{
            input: {
              cursor: "default",
              backgroundColor: isEmpty
                ? "var(--mantine-color-gray-0)"
                : undefined,
              color: isEmpty ? "var(--mantine-color-gray-5)" : undefined,
            },
          }}
        />
      </Box>
    );
  }

  // Handle clickable values (email, phone, URL)
  if (clickableInfo) {
    return (
      <Box>
        <Text size="sm" fw={500} mb={4}>
          {label}
        </Text>
        <TextInput
          value={stringValue}
          readOnly
          leftSection={IconSection}
          rightSection={
            <Group gap={4} wrap="nowrap">
              <Tooltip label="Open" withArrow>
                <ActionIcon
                  variant="subtle"
                  color="blue"
                  size="sm"
                  component="a"
                  href={clickableInfo.href}
                  target={clickableInfo.target}
                  rel={
                    clickableInfo.target === "_blank"
                      ? "noopener noreferrer"
                      : undefined
                  }
                >
                  <AppIcon iconName="ExternalLink" size={14} />
                </ActionIcon>
              </Tooltip>
              {CopyButtonSection}
            </Group>
          }
          rightSectionWidth={hideCopyButton ? 32 : 60}
          styles={{
            input: { cursor: "default" },
          }}
        />
      </Box>
    );
  }

  // Default: regular TextInput
  return (
    <Box>
      <Text size="sm" fw={500} mb={4}>
        {label}
      </Text>
      <TextInput
        value={isEmpty ? "—" : stringValue}
        readOnly
        leftSection={IconSection}
        rightSection={CopyButtonSection}
        rightSectionWidth={hideCopyButton ? 0 : 32}
        styles={{
          input: {
            cursor: "default",
            backgroundColor: isEmpty
              ? "var(--mantine-color-gray-0)"
              : undefined,
            color: isEmpty ? "var(--mantine-color-gray-5)" : undefined,
          },
        }}
      />
    </Box>
  );
};

// File card component for inline file display
interface FileCardProps {
  file: SubmissionFile;
  onPreview: (file: SubmissionFile) => void;
  onDownload: (file: SubmissionFile) => void;
  hideActions?: boolean;
}

const FileCard: React.FC<FileCardProps> = ({
  file,
  onPreview,
  onDownload,
  hideActions = false,
}) => {
  const hasError = !!file.errorDescription;
  const canPreview = isImageFile(file) || isVideoFile(file);

  if (hasError) {
    return (
      <Paper withBorder p="sm" bg="red.0">
        <Stack gap="xs">
          <Group gap="xs" wrap="nowrap">
            <AppIcon
              iconName="AlertCircle"
              size={16}
              color="red.6"
              style={{ flexShrink: 0 }}
            />
            <Text
              size="sm"
              fw={500}
              style={{ wordBreak: "break-word", flex: 1 }}
            >
              {file.fileName || "Unknown file"}
            </Text>
            <Badge variant="light" color="red" size="xs">
              Failed
            </Badge>
          </Group>
          {file.errorDescription && (
            <Text size="xs" c="red.7">
              {file.errorDescription}
            </Text>
          )}
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper withBorder p="sm">
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap" gap="xs">
          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" fw={500} style={{ wordBreak: "break-word" }}>
              {file.fileName || "Unknown file"}
            </Text>
            <Group gap="xs" wrap="wrap">
              <Text size="xs" c="dimmed">
                {file.fileSize ? formatFileSize(file.fileSize) : "—"}
              </Text>
              {file.mimeType && (
                <>
                  <Text size="xs" c="dimmed">
                    •
                  </Text>
                  <Text size="xs" c="dimmed" style={{ wordBreak: "break-all" }}>
                    {file.mimeType}
                  </Text>
                </>
              )}
            </Group>
          </Stack>
          {!hideActions && (
            <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
              {canPreview && (
                <Tooltip label="Preview" withArrow>
                  <ActionIcon
                    variant="light"
                    size="sm"
                    onClick={() => onPreview(file)}
                  >
                    <AppIcon iconName="Eye" size={14} />
                  </ActionIcon>
                </Tooltip>
              )}
              <Tooltip label="Download" withArrow>
                <ActionIcon
                  variant="light"
                  size="sm"
                  onClick={() => onDownload(file)}
                >
                  <AppIcon iconName="Download" size={14} />
                </ActionIcon>
              </Tooltip>
            </Group>
          )}
        </Group>

        {/* Image thumbnail preview */}
        {isImageFile(file) && file.fileUrl && (
          <Box
            style={{
              maxHeight: 150,
              overflow: "hidden",
              borderRadius: "var(--mantine-radius-sm)",
              cursor: hideActions ? "default" : "pointer",
            }}
            onClick={hideActions ? undefined : () => onPreview(file)}
          >
            <Image
              src={file.fileUrl}
              alt={file.fileName || "Preview"}
              fit="cover"
              style={{ maxHeight: 150 }}
            />
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

// File field component for displaying multiple files
interface FileFieldProps {
  label: string;
  files: SubmissionFile[];
  fieldType?: number;
  onPreview: (file: SubmissionFile) => void;
  onDownload: (file: SubmissionFile) => void;
  hideActions?: boolean;
}

const FileField: React.FC<FileFieldProps> = ({
  label,
  files,
  fieldType,
  onPreview,
  onDownload,
  hideActions = false,
}) => {
  const successfulFiles = files.filter((f) => !f.errorDescription);
  const failedFiles = files.filter((f) => f.errorDescription);
  const totalSize = successfulFiles.reduce((sum, f) => {
    return sum + (f.fileSize ? parseInt(f.fileSize, 10) : 0);
  }, 0);

  return (
    <Box>
      <Group justify="space-between" align="center" mb="xs">
        <Group gap="xs">
          <FormFieldTypeIcon
            fieldType={fieldType}
            size={16}
            color="var(--mantine-color-gray-5)"
          />
          <Text size="sm" fw={500}>
            {label}
          </Text>
          <Badge variant="light" color="gray" size="xs">
            {files.length} file{files.length !== 1 ? "s" : ""}
          </Badge>
        </Group>
        {totalSize > 0 && (
          <Text size="xs" c="dimmed">
            Total: {formatFileSize(totalSize)}
          </Text>
        )}
      </Group>

      <Stack gap="xs">
        {/* Show failed files first */}
        {failedFiles.map((file) => (
          <FileCard
            key={file.id}
            file={file}
            onPreview={onPreview}
            onDownload={onDownload}
            hideActions={hideActions}
          />
        ))}

        {/* Successful files */}
        {successfulFiles.length <= 2 ? (
          // Show as stack for 1-2 files
          successfulFiles.map((file) => (
            <FileCard
              key={file.id}
              file={file}
              onPreview={onPreview}
              onDownload={onDownload}
              hideActions={hideActions}
            />
          ))
        ) : (
          // Show as grid for 3+ files
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
            {successfulFiles.map((file) => (
              <FileCard
                key={file.id}
                file={file}
                onPreview={onPreview}
                onDownload={onDownload}
                hideActions={hideActions}
              />
            ))}
          </SimpleGrid>
        )}
      </Stack>
    </Box>
  );
};

// JSON View Modal component
interface JsonViewModalProps {
  opened: boolean;
  onClose: () => void;
  jsonString: string;
  onDownload: () => void;
}

const JsonViewModal: React.FC<JsonViewModalProps> = ({
  opened,
  onClose,
  jsonString,
  onDownload,
}) => (
  <Modal
    opened={opened}
    onClose={onClose}
    title={
      <Group gap="xs">
        <AppIcon iconName="Braces" size={20} />
        <Text fw={500}>Submission JSON</Text>
      </Group>
    }
    size="lg"
    centered
  >
    <Stack gap="md">
      <ScrollArea h={400} type="auto">
        <Code block style={{ fontSize: "0.8rem", whiteSpace: "pre-wrap" }}>
          {jsonString}
        </Code>
      </ScrollArea>

      <Group justify="flex-end" gap="xs">
        <CopyButton value={jsonString}>
          {({ copied, copy }) => (
            <Button
              variant="light"
              color={copied ? "teal" : "blue"}
              leftSection={
                <AppIcon iconName={copied ? "Check" : "Copy"} size={16} />
              }
              onClick={copy}
            >
              {copied ? "Copied!" : "Copy JSON"}
            </Button>
          )}
        </CopyButton>
        <Button
          variant="light"
          leftSection={<AppIcon iconName="Download" size={16} />}
          onClick={onDownload}
        >
          Download JSON
        </Button>
        <Button variant="default" onClick={onClose}>
          Close
        </Button>
      </Group>
    </Stack>
  </Modal>
);

// ============================================================================
// Main Component
// ============================================================================

export const SubmissionFormData: React.FC<SubmissionFormDataProps> = ({
  submission,
  form,
  formFields,
  hideCopyButtons = false,
  hideFileActions = false,
}) => {
  // File preview modal state
  const [previewFile, setPreviewFile] = useState<SubmissionFile | null>(null);

  // JSON view modal state
  const [jsonModalOpened, setJsonModalOpened] = useState(false);

  // Create field configuration maps
  const { fieldLabels, fieldTypes, fieldOptions, fieldOrder } = useMemo(() => {
    const labels: Record<string, string> = {};
    const types: Record<string, number> = {};
    const options: Record<string, string | undefined> = {};
    const order: string[] = [];
    const fields = formFields || form?.formFields || [];

    fields.forEach((field) => {
      if (field.name) {
        labels[field.name] = field.label || field.name;
        if (field.fieldType !== undefined) {
          types[field.name] = field.fieldType;
        }
        options[field.name] = field.options;
        order.push(field.name);
      }
    });

    return {
      fieldLabels: labels,
      fieldTypes: types,
      fieldOptions: options,
      fieldOrder: order,
    };
  }, [formFields, form?.formFields]);

  // Parse form data and group files by field name
  const { formDataValues, filesByField, orphanedFields, orphanedFiles } =
    useMemo(() => {
      const values: Record<string, any> = {};
      const filesMap: Record<string, SubmissionFile[]> = {};
      const orphanedFieldList: string[] = [];
      const orphanedFileList: SubmissionFile[] = [];

      // Parse form data
      if (submission?.formData) {
        try {
          const parsed = JSON.parse(submission.formData);
          Object.entries(parsed).forEach(([key, value]) => {
            values[key] = value;
            if (!fieldOrder.includes(key)) {
              orphanedFieldList.push(key);
            }
          });
        } catch (error) {
          console.error("Failed to parse formData:", error);
        }
      }

      // Group files by field name
      (submission?.submissionFiles || []).forEach((file) => {
        const fieldName = file.formFieldName || "_unassigned";
        if (!filesMap[fieldName]) {
          filesMap[fieldName] = [];
        }
        filesMap[fieldName].push(file);

        // Check for orphaned files
        if (fieldName !== "_unassigned" && !fieldOrder.includes(fieldName)) {
          orphanedFileList.push(file);
        }
      });

      // Add unassigned files to orphaned list
      if (filesMap["_unassigned"]) {
        orphanedFileList.push(...filesMap["_unassigned"]);
      }

      return {
        formDataValues: values,
        filesByField: filesMap,
        orphanedFields: orphanedFieldList,
        orphanedFiles: orphanedFileList,
      };
    }, [submission?.formData, submission?.submissionFiles, fieldOrder]);

  // Handle file download
  const handleDownloadFile = useCallback((file: SubmissionFile) => {
    if (!file.fileUrl) return;
    const link = document.createElement("a");
    link.href = file.fileUrl;
    link.download = file.fileName || "download";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  // Handle file preview
  const handlePreviewFile = useCallback(
    (file: SubmissionFile) => {
      if (isImageFile(file) || isVideoFile(file)) {
        setPreviewFile(file);
      } else {
        handleDownloadFile(file);
      }
    },
    [handleDownloadFile]
  );

  // Build structured JSON object for export
  const submissionJson = useMemo(() => {
    const metadata: Record<string, any> = {};

    // Add metadata fields
    if (submission?.id) metadata.submissionId = submission.id;
    if (submission?.requestId) metadata.requestId = submission.requestId;
    if (submission?.submissionDttm) {
      metadata.submittedAt = submission.submissionDttm;
      metadata.submittedAtFormatted = formatDateTime(
        submission.submissionDttm,
        "datetime"
      );
    }
    if (submission?.status !== undefined) metadata.status = submission.status;
    if (submission?.remoteIp) metadata.ipAddress = submission.remoteIp;
    if (submission?.referrer) metadata.referrer = submission.referrer;
    if (submission?.userAgent) metadata.userAgent = submission.userAgent;

    // Spam protection data
    if (submission?.spamScore !== undefined)
      metadata.spamScore = submission.spamScore;
    if (submission?.gibberishScore !== undefined)
      metadata.gibberishScore = submission.gibberishScore;
    if (submission?.spamReason) metadata.spamReason = submission.spamReason;
    if (submission?.spamEngine) metadata.spamEngine = submission.spamEngine;

    // Files metadata
    const files = (submission?.submissionFiles || []).map((file) => ({
      id: file.id,
      fieldName: file.formFieldName,
      fileName: file.fileName,
      fileSize: file.fileSize ? parseInt(file.fileSize, 10) : null,
      mimeType: file.mimeType,
      fileUrl: file.fileUrl,
      ...(file.errorDescription ? { error: file.errorDescription } : {}),
    }));

    if (files.length > 0) {
      metadata.files = files;
    }

    return {
      metadata,
      formData: formDataValues,
    };
  }, [submission, formDataValues]);

  // JSON string for display and copy
  const jsonString = useMemo(() => {
    return JSON.stringify(submissionJson, null, 2);
  }, [submissionJson]);

  // Handle JSON download
  const handleDownloadJson = useCallback(() => {
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `submission-${
      submission?.requestId || submission?.id || "data"
    }.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [jsonString, submission?.requestId, submission?.id]);

  // Check if we have any data
  const hasData =
    Object.keys(formDataValues).length > 0 ||
    Object.keys(filesByField).length > 0;

  const hasOrphanedData = orphanedFields.length > 0 || orphanedFiles.length > 0;

  if (!hasData) {
    return (
      <>
        <Stack gap="md">
          <Group justify="space-between" align="center">
            <Text size="sm" fw={600} tt="uppercase" c="dimmed">
              Form Data
            </Text>
            <Tooltip label="View as JSON" withArrow>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={() => setJsonModalOpened(true)}
              >
                <AppIcon iconName="Braces" size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
          <Text c="dimmed" size="sm">
            No form data available
          </Text>
        </Stack>

        <JsonViewModal
          opened={jsonModalOpened}
          onClose={() => setJsonModalOpened(false)}
          jsonString={jsonString}
          onDownload={handleDownloadJson}
        />
      </>
    );
  }

  return (
    <>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Text size="sm" fw={600} tt="uppercase" c="dimmed">
            Form Data
          </Text>
          <Tooltip label="View as JSON" withArrow>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              onClick={() => setJsonModalOpened(true)}
            >
              <AppIcon iconName="Braces" size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>

        {hasOrphanedData && (
          <Alert
            icon={<AppIcon iconName="AlertTriangle" size={16} />}
            color="yellow"
            variant="light"
          >
            <Text size="sm">
              Some fields or files in this submission are not configured in the
              current form definition.
            </Text>
          </Alert>
        )}

        <Stack gap="lg">
          {/* Render fields in configured order */}
          {fieldOrder.map((fieldName) => {
            const fieldType = fieldTypes[fieldName];
            const label = fieldLabels[fieldName] || fieldName;
            const isFileField = fieldType === FIELD_TYPE_IDS.FILE;
            const isCoordinatesField = fieldType === FIELD_TYPE_IDS.COORDINATES;
            const files = filesByField[fieldName];
            const value = formDataValues[fieldName];

            // File field
            if (isFileField && files && files.length > 0) {
              return (
                <FileField
                  key={fieldName}
                  label={label}
                  files={files}
                  fieldType={fieldType}
                  onPreview={handlePreviewFile}
                  onDownload={handleDownloadFile}
                  hideActions={hideFileActions}
                />
              );
            }

            // Skip if no value and not a file field
            if (value === undefined && !isFileField) {
              return null;
            }

            // Coordinates field - show with map
            if (isCoordinatesField) {
              return (
                <CoordinatesFieldValue
                  key={fieldName}
                  label={label}
                  value={value}
                  fieldOptions={fieldOptions[fieldName]}
                  hideCopyButton={hideCopyButtons}
                />
              );
            }

            // Regular field
            return (
              <FieldValueInput
                key={fieldName}
                fieldName={fieldName}
                label={label}
                value={value}
                fieldType={fieldType}
                hideCopyButton={hideCopyButtons}
              />
            );
          })}

          {/* Render orphaned fields */}
          {orphanedFields.length > 0 && (
            <Box
              p="md"
              style={{
                backgroundColor: "var(--mantine-color-yellow-0)",
                borderRadius: "var(--mantine-radius-sm)",
              }}
            >
              <Group gap="xs" mb="md">
                <AppIcon iconName="AlertTriangle" size={16} color="yellow.6" />
                <Text size="sm" fw={500} c="yellow.8">
                  Fields not in form configuration
                </Text>
              </Group>
              <Stack gap="md">
                {orphanedFields.map((fieldName) => (
                  <FieldValueInput
                    key={fieldName}
                    fieldName={fieldName}
                    label={fieldName}
                    value={formDataValues[fieldName]}
                    hideCopyButton={hideCopyButtons}
                    isOrphaned
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Render orphaned files */}
          {orphanedFiles.length > 0 && (
            <Box
              p="md"
              style={{
                backgroundColor: "var(--mantine-color-yellow-0)",
                borderRadius: "var(--mantine-radius-sm)",
              }}
            >
              <Group gap="xs" mb="md">
                <AppIcon iconName="AlertTriangle" size={16} color="yellow.6" />
                <Text size="sm" fw={500} c="yellow.8">
                  Files not associated with configured fields
                </Text>
              </Group>
              <Stack gap="xs">
                {orphanedFiles.map((file) => (
                  <FileCard
                    key={file.id}
                    file={file}
                    onPreview={handlePreviewFile}
                    onDownload={handleDownloadFile}
                    hideActions={hideFileActions}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </Stack>

      {/* File Preview Modal */}
      <Modal
        opened={!!previewFile}
        onClose={() => setPreviewFile(null)}
        title={previewFile?.fileName || "File Preview"}
        size="xl"
        centered
        styles={{
          body: { padding: 0 },
        }}
      >
        {previewFile && (
          <Stack gap="md" p="md">
            {isImageFile(previewFile) && (
              <Image
                src={previewFile.fileUrl}
                alt={previewFile.fileName || "Preview"}
                fit="contain"
                style={{
                  maxHeight: "70vh",
                  width: "100%",
                }}
              />
            )}
            {isVideoFile(previewFile) && (
              <video
                controls
                style={{
                  maxHeight: "70vh",
                  width: "100%",
                }}
              >
                <source src={previewFile.fileUrl} type={previewFile.mimeType} />
                Your browser does not support the video tag.
              </video>
            )}
            <Group justify="flex-end" gap="xs">
              <Button
                variant="light"
                leftSection={<AppIcon iconName="Download" size={16} />}
                onClick={() => handleDownloadFile(previewFile)}
              >
                Download
              </Button>
              <Button variant="default" onClick={() => setPreviewFile(null)}>
                Close
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <JsonViewModal
        opened={jsonModalOpened}
        onClose={() => setJsonModalOpened(false)}
        jsonString={jsonString}
        onDownload={handleDownloadJson}
      />
    </>
  );
};
